import { useMemo, useRef, useEffect, useState } from 'react';
import { ScheduleEvent, ScheduleHoliday } from '../../services/hooks/useClientPortal';
import { ClassVariant } from './ClientClassDetailModal';

// Mobile view of the client calendar: a horizontal strip of days on top, and the
// classes of the selected day below. Tapping a class opens the shared detail
// modal (same cancel / book-recovery actions as the desktop grid).

interface MobileDayAgendaProps {
    events: ScheduleEvent[];
    /**
     * P5 — the centre's holidays. They enter the day strip EVEN WITH no classes:
     * the strip was built from `events` alone, so a holiday (which since P3 has
     * none) simply vanished and the student saw the week jump from Monday to
     * Wednesday with no explanation — exactly the gap P5 exists to explain.
     */
    holidays?: ScheduleHoliday[];
    getVariant: (e: ScheduleEvent) => ClassVariant;
    onSelect: (event: ScheduleEvent, variant: ClassVariant) => void;
}

type PopulatedRef = { _id?: string; nombre?: string } | string | undefined;
const refName = (ref: PopulatedRef): string =>
    (typeof ref === 'object' && ref?.nombre ? ref.nombre : typeof ref === 'string' ? ref : '');

const DIAS_CORTOS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** "2026-07-22T00:00:00.000Z" → "2026-07-22" (the stored calendar day). */
const dayKey = (fecha: string): string => fecha.split('T')[0];

/** Parse a "YYYY-MM-DD" key as a LOCAL date (avoids UTC off-by-one). */
const parseDayKey = (key: string): Date => {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const todayKey = (): string => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

// Per-variant presentation for the class rows.
const VARIANT_STYLE: Record<ClassVariant, { bar: string; chip: string; label: string }> = {
    enrolled: { bar: 'bg-green-400', chip: 'bg-green-100 text-green-800', label: '✓ Inscrito' },
    bookable: { bar: 'bg-blue-400', chip: 'bg-blue-100 text-blue-800', label: '+ Reservar' },
    // Rojo solo para "completa" (sin sitio); el resto de no reservables, gris.
    full: { bar: 'bg-red-400', chip: 'bg-red-100 text-red-700', label: 'Completa' },
    locked: { bar: 'bg-gray-300', chip: 'bg-gray-100 text-gray-500', label: '' },
};

// D1 — a full class the client is queued for: waiting, not blocked.
const WAITLIST_STYLE = { bar: 'bg-amber-400', chip: 'bg-amber-100 text-amber-800', label: '⏳ En espera' };

const MobileDayAgenda = ({ events, holidays = [], getVariant, onSelect }: MobileDayAgendaProps) => {
    // Group the schedule by calendar day, keeping only today onward.
    const { days, byDay, holidayByDay } = useMemo(() => {
        const map = new Map<string, ScheduleEvent[]>();
        const today = todayKey();
        for (const e of events) {
            const key = dayKey(e.fecha);
            if (key < today) continue; // schedule is future-only, but be safe
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(e);
        }
        for (const list of map.values()) {
            list.sort((a, b) => a.horaInicio.localeCompare(b.horaInicio));
        }

        // P5 — holidays from today onward become days of their own in the strip. A
        // holiday that does have classes (older data, or one marked before P3)
        // keeps them: the day is labelled, not emptied.
        const byDayHoliday = new Map<string, ScheduleHoliday>();
        for (const h of holidays) {
            const key = dayKey(h.fecha);
            if (key < today) continue;
            byDayHoliday.set(key, h);
            if (!map.has(key)) map.set(key, []);
        }

        return { days: Array.from(map.keys()).sort(), byDay: map, holidayByDay: byDayHoliday };
    }, [events, holidays]);

    const [selectedDay, setSelectedDay] = useState<string | null>(null);
    // Default to today when it has classes, otherwise the next day in the strip —
    // which since P5 may be a holiday with no classes. Landing there is the point:
    // "today is a holiday" is exactly what the client came to find out.
    const activeDay = selectedDay && byDay.has(selectedDay) ? selectedDay : days[0] ?? null;

    // Keep the selected chip in view when it changes.
    const stripRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const el = stripRef.current?.querySelector('[data-active="true"]');
        el?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }, [activeDay]);

    if (days.length === 0) {
        return <p className="text-gray-500 text-sm py-6 text-center">No hay clases programadas.</p>;
    }

    const dayEvents = activeDay ? byDay.get(activeDay) ?? [] : [];
    const activeHoliday = activeDay ? holidayByDay.get(activeDay) : undefined;

    return (
        <div>
            {/* Tira de días */}
            <div ref={stripRef} className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
                {days.map((key) => {
                    const d = parseDayKey(key);
                    const isActive = key === activeDay;
                    const isToday = key === todayKey();
                    // Marca los días en los que el cliente ya está inscrito.
                    const hasEnrolled = (byDay.get(key) ?? []).some((e) => getVariant(e) === 'enrolled');
                    return (
                        <button
                            key={key}
                            data-active={isActive}
                            onClick={() => setSelectedDay(key)}
                            className={`shrink-0 snap-start w-14 py-2 rounded-lg border text-center transition-colors ${
                                isActive
                                    ? 'bg-brand-primary text-white border-transparent'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                            <div className={`text-[11px] ${isActive ? 'text-white/80' : 'text-gray-400'}`}>
                                {DIAS_CORTOS[d.getDay()]}
                            </div>
                            <div className="text-lg font-bold leading-tight">{d.getDate()}</div>
                            <div className={`text-[10px] ${isActive ? 'text-white/80' : 'text-gray-400'}`}>
                                {MESES[d.getMonth()]}
                            </div>
                            <div className="h-1.5 mt-0.5 flex items-center justify-center gap-0.5">
                                {hasEnrolled && (
                                    <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-white' : 'bg-green-500'}`} />
                                )}
                                {/* P5 — the holiday is flagged on the strip itself: if it
                                    were only said on opening the day, you would have to tap
                                    through days to find why a class is missing. */}
                                {holidayByDay.has(key) && (
                                    <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-white' : 'bg-gray-400'}`} />
                                )}
                                {isToday && !isActive && <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Clases del día seleccionado */}
            <div className="mt-4 space-y-2">
                {activeDay && (
                    <p className="text-sm font-semibold text-gray-700 capitalize">
                        {(() => {
                            const d = parseDayKey(activeDay);
                            return `${DIAS_CORTOS[d.getDay()]} ${d.getDate()} de ${MESES[d.getMonth()]}`;
                        })()}
                    </p>
                )}

                {/* P5 — the reason, carrying the centre's own name for the holiday when
                    it set one ("Fiesta Nacional"); otherwise just "Festivo". */}
                {activeHoliday && (
                    <p className="rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-700">
                        <strong>Festivo</strong>
                        {activeHoliday.descripcion ? ` — ${activeHoliday.descripcion}` : ''}
                    </p>
                )}

                {dayEvents.length === 0 ? (
                    <p className="text-gray-500 text-sm py-4">
                        {activeHoliday ? 'El centro no abre este día.' : 'No hay clases este día.'}
                    </p>
                ) : (
                    dayEvents.map((e) => {
                        const variant = getVariant(e);
                        const style = variant === 'full' && e.onWaitlist ? WAITLIST_STYLE : VARIANT_STYLE[variant];
                        // Ocupación como "ocupadas/total" (p. ej. 2/4).
                        const ocupacion =
                            typeof e.availableSlots === 'number' && typeof e.maxClientes === 'number'
                                ? `${Math.max(0, e.maxClientes - e.availableSlots)}/${e.maxClientes}`
                                : null;
                        return (
                            <button
                                key={e._id}
                                onClick={() => onSelect(e, variant)}
                                className="w-full flex items-stretch gap-3 text-left bg-white border border-gray-200 rounded-lg overflow-hidden hover:bg-gray-50 active:bg-gray-100"
                            >
                                <span className={`w-1.5 shrink-0 ${style.bar}`} />
                                <div className="flex-1 min-w-0 py-3 pr-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-semibold text-gray-800 text-sm">
                                                {e.horaInicio}–{e.horaFin}
                                            </div>
                                            <div className="text-sm text-gray-700 truncate">
                                                {refName(e.nivel_id as PopulatedRef) || 'Clase'}
                                            </div>
                                            <div className="text-xs text-gray-500 truncate">
                                                {[e.sala && `Sala ${e.sala}`, refName(e.instructor_id as PopulatedRef)]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            {style.label && (
                                                <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold ${style.chip}`}>
                                                    {style.label}
                                                </span>
                                            )}
                                            {ocupacion && (
                                                <div className="text-[11px] text-gray-500 mt-1">{ocupacion}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default MobileDayAgenda;
